import { useMutation } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import type { audience } from '@bb/contracts';
import { Button } from '@bb/ui';
import { ApiError, type AdminApi, type ExplainedNode } from '../../api/client.ts';

type By = audience.LookupAudienceBody['profile']['by'];
const BY_LABEL: Record<By, string> = { external_id: 'External ID', profile_id: 'Profile ID', email: 'Email', phone: 'Phone' };

/** A value a filter read, as text: `not set`, a quoted string, or JSON. */
export function formatActual(node: { actual?: unknown; actualMasked?: boolean }): string | null {
  if (node.actualMasked) return 'hidden (personal data)';
  if (node.actual === undefined) return null;
  if (node.actual === null) return 'not set';
  return typeof node.actual === 'string' ? `“${node.actual}”` : JSON.stringify(node.actual);
}

function NodeItem({ node }: { node: ExplainedNode }) {
  const actual = formatActual(node);
  return (
    <li data-result={node.result ? 'pass' : 'fail'}>
      <span aria-hidden="true">{node.result ? '✓' : '✗'}</span> {node.label ? <strong>{node.label}: </strong> : null}
      {node.sentence}
      {actual && <span className="hint"> — actual: {actual}</span>}
      {node.children && node.children.length > 0 && node.kind !== 'filter' && (
        <ul>
          {node.children.map((child) => (
            <NodeItem key={child.nodeId} node={child} />
          ))}
        </ul>
      )}
    </li>
  );
}

const errorText = (error: unknown) => {
  if (error instanceof ApiError && error.code === 'profile_not_found') return 'No such user in this environment.';
  if (error instanceof ApiError && error.code === 'permission_denied') return 'Looking up by email or phone needs the pii.view permission.';
  if (error instanceof ApiError && error.code === 'invalid_audience') return 'Fix the audience first.';
  return 'The user could not be checked.';
};

/**
 * User Lookup (BB-45): checks one user against the audience being edited and shows why they are in or out — the
 * failing filters with the values they read, matching exclusions, app targeting and the whole tree with results.
 */
export function UserLookupPanel({ environmentId, definition, locale = 'en' }: { environmentId: string; definition: audience.AudienceDefinition; locale?: 'uk' | 'en' }) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const [by, setBy] = useState<By>('external_id');
  const [value, setValue] = useState('');
  const lookup = useMutation({ mutationFn: () => api.lookupAudience(environmentId, definition, { by, value: value.trim() }, locale) });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (value.trim()) lookup.mutate();
  };
  const result = lookup.data;
  const who = result ? (result.profile.externalId ?? result.profile.id) : '';

  return (
    <section aria-label="Check a user" className="user-lookup">
      <h3>Check a user</h3>
      <form onSubmit={submit}>
        <select aria-label="Look up by" value={by} onChange={(e) => setBy(e.target.value as By)}>
          {(Object.keys(BY_LABEL) as By[]).map((key) => (
            <option key={key} value={key}>
              {BY_LABEL[key]}
            </option>
          ))}
        </select>
        <input aria-label="User" value={value} placeholder={by === 'email' ? 'user@example.com' : by === 'phone' ? '+380…' : ''} onChange={(e) => setValue(e.target.value)} />
        <Button type="submit" size="sm" disabled={lookup.isPending || !value.trim()}>
          Check
        </Button>
      </form>
      {lookup.error && <p role="alert">{errorText(lookup.error)}</p>}
      {result && (
        <div className={`user-lookup__result user-lookup__result--${result.match ? 'match' : 'miss'}`}>
          <p role="status">{result.match ? `✓ ${who} matches the audience` : `✗ ${who} does not match the audience`}</p>
          {result.profile.mergedFrom && <p className="hint">The profile you looked up was merged into this one.</p>}
          {result.blockers.length > 0 && (
            <ul aria-label="Why not">
              {result.blockers.map((b, i) => {
                const actual = formatActual(b);
                return (
                  <li key={`${b.nodeId ?? b.reason}-${i}`}>
                    {b.reason === 'excluded' ? 'Excluded by: ' : b.reason === 'not_matched' ? 'Not matched: ' : ''}
                    {b.sentence}
                    {actual && <span className="hint"> — actual: {actual}</span>}
                  </li>
                );
              })}
            </ul>
          )}
          {result.exclusions.length > 0 && (
            <section aria-label="Never messaged">
              <h4>Never messaged</h4>
              <ul>
                {result.exclusions.map((x) => (
                  <li key={x.kind}>{x.reason}</li>
                ))}
              </ul>
            </section>
          )}
          <details>
            <summary>All conditions</summary>
            <ul aria-label="Conditions">
              <NodeItem node={result.include} />
            </ul>
            {result.exclude.length > 0 && (
              <ul aria-label="Exclusion groups">
                {result.exclude.map((x) => (
                  <li key={x.nodeId} data-result={x.result ? 'fail' : 'pass'}>
                    {x.result ? '✗ Excluded by' : '✓ Not excluded by'} {x.label ? `${x.label}: ` : ''}
                    {x.sentence}
                  </li>
                ))}
              </ul>
            )}
          </details>
        </div>
      )}
    </section>
  );
}
